import type {IntlShape} from 'react-intl';
import type {PermissionType} from '@/Permissions';

// Pages and row types are named by the pages that host agents, so these components never list them.
export type AgentPage = string;
export type AgentRowType = string;

export const AGENT_RUN_SCOPE = {
	PAGE: 'PAGE',
	ROW: 'ROW',
} as const;

export const AGENT_RUN_STATE = {
	RUNNING: 'RUNNING',
	STOPPED: 'STOPPED',
	NOTHING_FOUND: 'NOTHING_FOUND',
	PRODUCED: 'PRODUCED',
	FAILED: 'FAILED',
	NOT_STARTED: 'NOT_STARTED',
} as const;
export type AgentRunState = (typeof AGENT_RUN_STATE)[keyof typeof AGENT_RUN_STATE];

export const AGENT_RESULT_STATE = {
	NEEDS_REVIEW: 'NEEDS_REVIEW',
	DONE: 'DONE',
	DELETED: 'DELETED',
} as const;
export type AgentResultState = (typeof AGENT_RESULT_STATE)[keyof typeof AGENT_RESULT_STATE];

export type AgentText = {
	id: string;
	defaultMessage: string;
};

// rowId is the record's database id, which is what the run records hold.
export type AgentRow = {
	rowType: AgentRowType;
	rowId: number;
};

export type AgentRunScope =
	| {
			kind: typeof AGENT_RUN_SCOPE.PAGE;
	  }
	| ({
			kind: typeof AGENT_RUN_SCOPE.ROW;
	  } & AgentRow);

export type AgentRun = {
	id: string;
	scope: AgentRunScope;
	state: AgentRunState;
};

export type AgentResult = {
	id: string;
	runId: string;
	rows: AgentRow[];
	state: AgentResultState;
	hidden: boolean;
};

export type AgentRunOutcome = {
	state: Exclude<AgentRunState, typeof AGENT_RUN_STATE.RUNNING | typeof AGENT_RUN_STATE.NOT_STARTED>;
	results: AgentResult[];
	detail: unknown;
};

export type AgentAcceptOutcome =
	| {
			accepted: true;
	  }
	| {
			accepted: false;
			reason: string;
	  };

export type AgentResultContent = {
	title: string;
	dateLine: string;
	total: string;
};

export type AgentDeclaration = {
	id: string;
	name: AgentText;
	pages: AgentPage[];
	rows: AgentRowType[];
	recordNoun: AgentText;
	permission: PermissionType;
	action: AgentText;
	resultNoun: {
		singular: AgentText;
		plural: AgentText;
	};
	acceptLabel: {
		single: AgentText;
		bulk: AgentText;
	};
	runAgainLabel: AgentText;
	runAgainToast: AgentText;
	doneStateName: AgentText;
	doneAction: {
		label: AgentText;
		perform: (result: AgentResult) => void;
	};
	rowActionTips: {
		idle: AgentText;
		working: AgentText;
		ready: AgentText;
	};
	describeResult: (result: AgentResult, intl: IntlShape) => AgentResultContent;
	foundText: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	summaryToast: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	notificationText: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	failureReason: (outcome: AgentRunOutcome, intl: IntlShape) => string;
	review: (result: AgentResult) => void;
	accept: (result: AgentResult) => Promise<AgentAcceptOutcome>;
	schedulingTarget: string;
};
